/**
 * Push Notifications (2026-08-21) - registers a device's FCM token so it can receive real
 * push notifications (income, withdrawal, bonus, rank, ticket etc.) even when the app is
 * closed/backgrounded, for every existing in-app notification type (see notification_service.py).
 *
 * Two sources of a token, exactly like the existing Android Razorpay bridge pattern:
 * - Native Android app: `window.AndroidFCMBridge.getToken()` (native FCM SDK, works when the
 *   app is fully closed/killed - see Android Studio doc for MyFirebaseMessagingService).
 * - Web/PWA browser: Firebase JS SDK + `public/firebase-messaging-sw.js` service worker.
 * Silently no-ops wherever unsupported/undenied/unconfigured - never blocks app usage.
 */
import { api } from "./api";

let _lastKnownToken = null;

export function isAndroidFcmBridgeAvailable() {
  const available = (
    typeof window !== "undefined" &&
    typeof window.AndroidFCMBridge !== "undefined" &&
    typeof window.AndroidFCMBridge.getToken === "function"
  );
  console.log("FCM_WEB_DEBUG: window.AndroidFCMBridge available =", available);
  return available;
}

async function getWebToken() {
  const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
  if (!apiKey || !("serviceWorker" in navigator) || typeof Notification === "undefined") {
    console.log("FCM_WEB_DEBUG: web push not supported/configured in this browser - skipping");
    return null;
  }
  try {
    const { initializeApp, getApps } = await import("firebase/app");
    const { getMessaging, getToken } = await import("firebase/messaging");
    const app = getApps().length
      ? getApps()[0]
      : initializeApp({
          apiKey,
          authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
          projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
          storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
          appId: process.env.REACT_APP_FIREBASE_APP_ID,
        });
    const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.log("FCM_WEB_DEBUG: Notification permission not granted:", permission);
      return null;
    }
    const messaging = getMessaging(app);
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    console.log("FCM_WEB_DEBUG: web FCM token obtained =", !!token);
    return token;
  } catch (e) {
    console.log("FCM_WEB_DEBUG: web FCM token retrieval FAILED:", e?.message || e);
    return null;
  }
}

export async function registerPushToken() {
  try {
    if (!localStorage.getItem("samrudhi_member_token")) return;
    let token = null;
    let platform = "web";
    if (isAndroidFcmBridgeAvailable()) {
      token = window.AndroidFCMBridge.getToken();
      platform = "android";
      console.log("FCM_WEB_DEBUG: token from AndroidFCMBridge.getToken() =", token ? "present" : "EMPTY/null");
    } else {
      token = await getWebToken();
    }
    if (token) {
      _lastKnownToken = token;
      console.log("FCM_WEB_DEBUG: registering token with backend, platform =", platform);
      await api.post("/member/fcm-token", { token, platform });
      console.log("FCM_WEB_DEBUG: token registration API call succeeded");
    } else {
      console.log("FCM_WEB_DEBUG: no token available to register (platform =", platform, ")");
    }
  } catch (e) {
    console.log("FCM_WEB_DEBUG: registerPushToken FAILED:", e?.message || e);
  }
}

// Called on member logout (BEFORE the auth token is cleared) so this device's push token is
// unlinked from the member who's signing out - prevents the next member who logs in on the
// same device/session from also being registered under the still-linked previous token,
// which was the root cause of cross-account notification leakage.
export async function unregisterPushToken() {
  try {
    let token = _lastKnownToken;
    if (!token && isAndroidFcmBridgeAvailable()) {
      token = window.AndroidFCMBridge.getToken();
    }
    if (!token) {
      console.log("FCM_WEB_DEBUG: unregisterPushToken - no known token to unregister");
      return;
    }
    await api.delete("/member/fcm-token", { params: { token } });
    console.log("FCM_WEB_DEBUG: token unregistered on logout");
  } catch (e) {
    console.log("FCM_WEB_DEBUG: unregisterPushToken FAILED:", e?.message || e);
  } finally {
    _lastKnownToken = null;
  }
}

// Called by the native Android app (webView.evaluateJavascript) whenever
// FirebaseMessagingService#onNewToken issues a refreshed token - see Android Studio doc.
if (typeof window !== "undefined") {
  window.onAndroidFcmTokenRefresh = (token) => {
    console.log("FCM_WEB_DEBUG: onAndroidFcmTokenRefresh called, token present =", !!token);
    if (token && localStorage.getItem("samrudhi_member_token")) {
      _lastKnownToken = token;
      api.post("/member/fcm-token", { token, platform: "android" })
        .then(() => console.log("FCM_WEB_DEBUG: refreshed token registered successfully"))
        .catch((e) => console.log("FCM_WEB_DEBUG: refreshed token registration FAILED:", e?.message || e));
    }
  };
}
